import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { PartViewer, VIEWER_BOX } from './PartViewer';
import type { ViewerOverlay } from './assemblyScene';
import { MeshModal } from './MeshModal';
import { MeshPlaceholder } from './MeshPlaceholder';
import { Database, Loader2, Trash2 } from 'lucide-react';
import { DownloadPill } from './DownloadPill';
import { dimensionLineSpecOf, passGroup, rebuiltOf, versionGroup, versionRootId } from './openaiBlenderApi';
import { downloadSrc, type Job3D } from './threeDApi';
import { DbSwitch } from './DbSwitch';
import { fixedFor, type CatalogSwitch } from './catalogSwitch';
import { useLanguage } from '../../contexts/LanguageContext';
import { builtDown } from './holeFit';

// A MESH, SMALL AND BIG — the panel-sized viewer and the one that fills the screen, as one
// thing rather than two that happen to be written near each other.
//
// PHASE 5 HAS TWO OF THESE: a part, and the whole product. The part's grew an expand button,
// a modal over the page, a pass toggle, the pictures it was modelled from, and a camera that
// survives the trip in and out. The product's had none of it — not because a product is
// different but because it was a second copy of the same code and nobody edited it that day.
// Reaching for the whole product and finding no way to look at it closely is that copy.
//
// So the pairing lives here: give this a mesh and you get both viewers, the button between
// them, and one key holding the camera across it. What the two callers put IN the modal
// differs — a part has four bands of controls, a product its dimensions and materials — and
// those are slots, which is a difference you can see. What does NOT differ is drawn here: the
// database switch, which the product went without for as long as it was a slot. Both have a size to be measured
// against: a part's from its row, a product's overall one from the same drawing.
const SPINNER = <Loader2 className="h-8 w-8 animate-spin text-slate-300" />;

// THE MODAL CORNER'S DELETE — a whole 3D model's, from the callers, and a version's, from here.
export function DeleteRunButton({ label, busy, onClick }: { label: string; busy: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className="inline-flex items-center gap-1.5 rounded-lg border border-rose-300 px-2.5 py-1.5 text-[12.5px] font-medium text-rose-600 transition-colors hover:border-rose-500 hover:bg-rose-50 disabled:cursor-not-allowed"
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
      {label}
    </button>
  );
}

// HOW LONG A FINISHED REPAINT WAITS FOR ITS NEW FILE before showing whatever there is: a mesh the
// apply skipped keeps its address, and the placeholder must not outlive the reason for it.
const REPAINT_WAIT_MS = 20_000;

// BUSY UNTIL THE NEW FILE IS HERE, not until the apply returns. The apply rewrites the file and the
// page learns its new address a round trip later (see bumpProductRuns), so in between the viewer
// came back on the OLD mesh — the colours being replaced, looking like the answer. Held while the
// address is the one it had when the work began.
function useBusyUntilNew(busy: boolean, src: string | null | undefined): boolean {
  const began = useRef<{ src: string | null | undefined } | null>(null);
  const [stale, setStale] = useState<{ src: string | null | undefined } | null>(null);
  useEffect(() => {
    if (busy) {
      began.current = { src };
      return;
    }
    if (!began.current) return;
    const was = began.current;
    began.current = null;
    if (was.src !== src) return;
    setStale(was);
    const timer = window.setTimeout(() => setStale(null), REPAINT_WAIT_MS);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy]);
  return busy || (!!stale && stale.src === src);
}

export function MeshStage({
  job, catalog, src, label, heading, took, stages, checks, keepView, expanded, onExpanded,
  dims, estimated, pieceWanted, trueScale, builtMm, sides, explodable, pieceLabel, pieceOrder, overlays, orientJobId,
  onMeasure, onProfile,
  passes, caption, strip, footer, corner, className = VIEWER_BOX,
  paused = false, busy: working = false, fileName,
}: {
  // THE NAME ITS GLB IS SAVED UNDER, without the extension. Absent, what it is and its run's number.
  fileName?: string;
  // THE RUN ON SCREEN, and the switch that keeps it in the database — see useCatalogSwitch.
  // Required, both: a panel that shows a mesh has the switch whether or not it remembered to.
  job: Job3D;
  catalog: CatalogSwitch;
  src: string | null;
  // What the thing on screen is called, for the viewer's own legend — without it the legend
  // reads "3D model", which is what every mesh in this app is.
  label?: string;
  // THE PANEL'S OWN HEADING AND THE GENERATION'S TIME, at the very top of the modal — see
  // MeshModal.
  heading?: string;
  took?: number | null;
  stages?: Parameters<typeof MeshModal>[0]['stages'];
  // WHAT THE CHECKS FOUND on the run on screen — see written_checks. Null for a run with none.
  checks?: string[] | null;
  // WHAT MAKES THE TWO VIEWERS ONE VIEW. Expanding unmounts the small viewer and mounts the
  // big one, so without a shared key the gesture you make in order to GO ON looking at
  // something is the one that throws away how you were looking at it. See PartViewer.
  keepView?: string;
  expanded: boolean;
  onExpanded: (open: boolean) => void;
  dims?: Parameters<typeof MeshModal>[0]['dims'];
  estimated?: boolean;
  // What each of the model's own meshes should measure, by name — see PartViewer.
  pieceWanted?: Parameters<typeof PartViewer>[0]['pieceWanted'];
  trueScale?: boolean;
  // What the build measured, for a run that measured itself — see PartViewer.
  builtMm?: { x: number; y: number; z: number } | null;
  sides?: boolean;
  // Whether this mesh is an assembly whose parts can be spread apart — see PartViewer.
  // 'axis' for hardware built together: apart along its one axis — see explodeShifts.
  explodable?: boolean | 'axis';
  // HOW A MESH'S ROW IS NAMED in the parts card, where that differs from its name — see ScenePartCards.
  pieceLabel?: (name: string) => string;
  // The order its rows are listed in — see ScenePartCards.
  pieceOrder?: string[];
  // Solids laid over the model — see PartViewer.
  overlays?: readonly ViewerOverlay[];
  orientJobId?: number;
  onMeasure?: (size: Parameters<NonNullable<Parameters<typeof PartViewer>[0]['onMeasure']>>[0]) => void;
  onProfile?: Parameters<typeof PartViewer>[0]['onProfile'];
  // THE PASSES OF THE RUN ON SCREEN, and the way to another one. A written run models the
  // thing more than once — a draft, then corrections made from renders of it — and the
  // question the set exists to answer is whether the later ones are better. That is a
  // question about pictures of the same object, so it is asked where the object fills the
  // screen and not between two thumbnails a few pixels apart.
  //
  // Drawn by this component and not by either caller, because it is the same control in
  // both and was on its way to being written twice.
  // `onDelete` forgets one version — a rebuild, never the pass itself, which is the 3D model's
  // own delete — and `deletingId` is the one being forgotten.
  passes?: {
    of: Job3D[]; shown: Job3D; onShow: (id: number) => void;
    onDelete?: (job: Job3D) => void; deletingId?: number | null;
  };
  // What the modal says the mesh IS, beside its own heading.
  caption?: ReactNode;
  // The pictures the mesh is a model OF, across the top of the big view.
  strip?: ReactNode;
  // Everything that acts on the mesh, under it. A part's is four bands of controls; a
  // product's is what a product can be asked.
  footer?: ReactNode;
  // The modal's own top-left, for what acts on the RUN rather than on its geometry.
  corner?: ReactNode;
  className?: string;
  // SOMETHING ELSE IS HOLDING THE SCREEN, so the small viewer is not mounted. The texture
  // retouch is the case: it has a viewer of its own, and this one would go on rendering the
  // same 475,000 triangles sixty times a second underneath a full-screen overlay nobody can
  // see it through — which was making the one being turned stutter. The big view is not
  // gated by it: closing the retouch has to come back to what was open.
  paused?: boolean;
  // THE MESH IS BEING REWRITTEN — its materials, say — so both viewers draw a spinner where it
  // is, rather than go on showing the file as it was until the new one lands.
  busy?: boolean;
}) {
  const busy = useBusyUntilNew(working, src);
  const { t } = useLanguage();
  // A COVER BUILT CONTACT FACE DOWN says so on that face (see builtDown).
  const contact = job && builtDown(job) ? 'bottom' as const : undefined;
  const group = passes ? passGroup(passes.shown, passes.of) : [];
  // Memoised on the run, so the viewer is not handed a new set of lines on every render.
  const lines = useMemo(() => dimensionLineSpecOf(job), [job]);
  // THE PASS ON SCREEN — the one the version shown is a version of — and ITS VERSIONS: the pass
  // itself first, then each rebuild of it at other sizes, in the order they were made.
  const passId = passes ? versionRootId(passes.shown, passes.of) : null;
  const versions = passes ? versionGroup(passes.shown, passes.of) : [];
  // Two toggles, one under the other, drawn in both places: under the small viewer and in the
  // modal's header. Each drawn for one too, as "pass 1/1" and "version 1/1": a run that made its
  // model once says so, rather than leaving the absence of the toggle to be read as the answer.
  const toggle = (items: Job3D[], onId: number | null, label: string, kept: (j: Job3D) => boolean) => (
    <span className="inline-flex overflow-hidden rounded-lg border border-slate-300">
      {items.map((p, i) => (
        <button
          key={p.id}
          type="button"
          onClick={() => passes?.onShow(p.id)}
          className={`px-2.5 py-1 text-[11px] font-semibold transition-colors ${
            p.id === onId
              ? 'bg-blue-600 text-white'
              : 'text-slate-500 hover:bg-slate-100'
          }`}
        >
          {t(label).replace('{n}', String(i + 1)).replace('{of}', String(items.length))}
          {/* WHICH IS KEPT — the database holds a mesh, not a run. */}
          {kept(p) && (
            <Database
              className="ml-1 inline h-3 w-3 -translate-y-px"
              aria-label={t('notice.3d.inDb')}
            />
          )}
        </button>
      ))}
    </span>
  );
  const picker = group.length > 0 && passes && (
    <span className="inline-flex flex-col items-center gap-1.5">
      {/* A pass is marked kept where any of its versions is. */}
      {toggle(group, passId, 'notice.3d.pass',
        (p) => versionGroup(p, passes.of).some((v) => v.inCatalog))}
      {toggle(versions, passes.shown.id, 'notice.3d.version', (v) => v.inCatalog)}
    </span>
  );
  // Under the 3D model's delete, while a version other than the pass is on screen. The pass is
  // shown once it is gone, rather than whichever run the list falls back to.
  const onDeleteVersion = passes?.onDelete;
  const versionDelete = passes && onDeleteVersion && passId != null && rebuiltOf(passes.shown) && passes.shown.id !== passId && (
    <DeleteRunButton
      label={t('notice.3d.deleteVersion')}
      busy={passes.deletingId === passes.shown.id}
      onClick={() => {
        const gone = passes.shown;
        if (passes.of.some((j) => j.id === passId)) passes.onShow(passId);
        onDeleteVersion(gone);
      }}
    />
  );

  return (
    <>
      {/* TORN DOWN WHILE THE BIG ONE IS UP. A viewer is a WebGL context and a copy of the
          mesh on the GPU, a browser grants a page a fixed number of the first, and the page
          already spends several on thumbnails — so two copies of a 460,000-triangle chair to
          look at one of them is not a trade. The camera and the pose survive it under
          `keepView`, which is what makes the tear-down free. */}
      {!expanded && !paused && busy && (
        <MeshPlaceholder label={t('notice.3d.repainting')} icon={SPINNER} className={className} />
      )}
      {!expanded && !paused && !busy && (
        <PartViewer
          contactFace={contact}
          src={src ?? ''}
          dims={dims}
          estimated={estimated}
          pieceWanted={pieceWanted}
          trueScale={trueScale}
          builtMm={builtMm}
          label={label}
          sides={sides}
          explodable={explodable}
          pieceLabel={pieceLabel}
          pieceOrder={pieceOrder}
          overlays={overlays}
          keepView={keepView}
          onMeasure={onMeasure}
          onProfile={onProfile}
          onExpand={() => onExpanded(true)}
          // THE FILE ITSELF, TO KEEP (Louis 2026-10-29): the GLB as on screen, textures and colourway
          // in it, saved under the name of what it is, the exploded view its animation where it has
          // one. Left of the picture.
          left={src && (
            <DownloadPill href={downloadSrc(src, fileName ?? `${label || 'model'} ${job.id}`,
              explodable === 'axis' ? 'z' : explodable ? 'centre' : undefined)}
            >
              {t('notice.3d.downloadGlb')}
            </DownloadPill>
          )}
          // ITS CONTROLS BESIDE IT, as the assembly phase has them, not over the part.
          controlsBeside
          className={className}
        />
      )}
      {/* WHICH PASS IS ON SCREEN, under the viewer on the page too. The strip's tile says
          "(2 passes)" and nothing else did outside the modal, so the page showed one of
          two meshes without saying which. */}
      {!expanded && !paused && picker && <div className="flex w-full justify-center">{picker}</div>}

      {/* The same viewer, over the page, at a size worth turning something in. Its own scene
          rather than this one made bigger: mounting a mesh is what the modal's viewer does on
          `src`, and there is no state here worth carrying across that keepView does not. */}
      {expanded && (
        <MeshModal
          src={busy ? null : src}
          emptyLabel={t('notice.3d.repainting')}
          emptyIcon={SPINNER}
          label={label}
          heading={heading}
          took={took}
          stages={stages}
          checks={checks}
          keepView={keepView}
          caption={caption}
          strip={strip}
          picker={picker}
          dims={dims}
          estimated={estimated}
          pieceWanted={pieceWanted}
          trueScale={trueScale}
          builtMm={builtMm}
          orientJobId={orientJobId}
          contactFace={contact}
          sides={sides}
          explodable={explodable}
          pieceLabel={pieceLabel}
          pieceOrder={pieceOrder}
          overlays={overlays}
          onMeasure={onMeasure}
          onProfile={onProfile}
          corner={versionDelete
            ? <div className="flex flex-col items-start gap-2">{corner}{versionDelete}</div>
            : corner}
          // KEEP THIS MESH, in the big view rather than only on the thumbnails: it is where a
          // mesh is judged, and the decision that follows judging it is this switch. Inside the
          // white, top-left, where it sits on the tile — not in the modal's own corner, which is
          // for what acts on the RUN; this is about the object you are looking at.
          viewerCorner={(
            <DbSwitch
              on={job.inCatalog}
              busy={catalog.busyId === job.id}
              disabled={!!fixedFor(catalog, job)}
              title={fixedFor(catalog, job) ?? t(job.inCatalog ? 'notice.3d.inDb' : 'notice.3d.addToDb')}
              size="md"
              onWhite
              onToggle={() => catalog.onToggle(job, !job.inCatalog)}
            />
          )}
          footer={footer}
          // WHERE THE FOOTER'S DIMENSIONS ARE MEASURED, on the model they size — the big view
          // only, where the numbers are.
          dimensionLines={lines}
          onClose={() => onExpanded(false)}
        />
      )}
    </>
  );
}
